/**
 * Trail-ul de acțiuni: ce a făcut vizitatorul pe sit ÎNAINTE să pună întrebarea
 * (și între întrebări) - navigări, click-uri pe elemente interactive, submit-uri
 * de formular. Se înregistrează de la init(), nu de la deschiderea chatului:
 * exact drumul de dinainte de primul mesaj e valoarea. Pleacă la fiecare /ask
 * ca `recentActions` (vezi shared/src/api.ts) și, mai târziu, va descrie
 * drumul până la eroare într-un support ticket.
 *
 * Colectarea începe la `init()`, dar scrierea în sessionStorage NU: până la
 * prima interacțiune explicită (deschiderea widgetului sau prima întrebare -
 * `flush()` de mai jos), evenimentele stau doar în memorie. Detaliile la
 * `installActionTrail`.
 *
 * Aceleași granițe ca snapshotul (privacy §2.2, registru → G12):
 *  - NICIODATĂ value din inputuri - eticheta unui buton e text randat de sit,
 *    ce a tastat omul nu se atinge; la submit reținem doar identitatea
 *    formularului, nu câmpurile;
 *  - pe o pagină exclusă (excludedUrls) nu se înregistrează nimic;
 *  - un click într-o zonă exclusă (excludedSelectors / data-aig-snapshot-exclude)
 *    nu se înregistrează;
 *  - click-urile pe propriul widget/builder nu sunt acțiuni pe sit - afară.
 */
import {
  ACTION_TRAIL_MAX_EVENTS,
  ACTION_TRAIL_MAX_LABEL_CHARS,
  type UserActionEvent,
} from "@ai-guidance/shared";
import { recordAction } from "./chatHistory";
import { currentPagePath } from "./page";
import { isExcludedElement } from "./snapshot";

/** Pe ce considerăm un click „o acțiune": elemente cu rol interactiv declarat. */
const INTERACTIVE_SELECTOR =
  'a[href],button,[role="button"],input[type="submit"],input[type="button"],summary,[role="tab"],[role="menuitem"]';

/**
 * Propriile noastre elemente din light DOM. Click-urile din interiorul
 * shadow root-ului widgetului ies retargetate pe gazda `.aig-root`, deci
 * verificarea pe light DOM le prinde și pe ele.
 */
const OWN_ELEMENTS_SELECTOR = "[data-ai-guidance],[data-aig-builder],.aig-root";

/**
 * Eticheta vizibilă a elementului - DOAR text randat sau atribute statice puse
 * de sit. `value` apare o singură dată, ca ATRIBUT (captionul static al unui
 * input[type=submit], scris în HTML) - niciodată proprietatea vie `.value`,
 * care ar putea conține text tastat de vizitator.
 */
export function labelFor(el: Element): string | undefined {
  const text =
    el.getAttribute("aria-label") ||
    el.textContent ||
    el.getAttribute("value") ||
    el.getAttribute("title") ||
    "";
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (!collapsed) return undefined;
  return collapsed.slice(0, ACTION_TRAIL_MAX_LABEL_CHARS);
}

/** Identitatea unui formular: atribute statice, niciodată conținutul câmpurilor. */
function formLabel(form: HTMLFormElement): string | undefined {
  const text =
    form.getAttribute("aria-label") || form.getAttribute("name") || form.getAttribute("id") || "";
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (!collapsed) return undefined;
  return collapsed.slice(0, ACTION_TRAIL_MAX_LABEL_CHARS);
}

/** Întors de `installActionTrail` - punctul prin care sdk.ts anunță prima interacțiune. */
export interface ActionTrailHandle {
  /**
   * Scrie în sessionStorage tot ce s-a acumulat până acum DOAR în memorie și
   * trece pe scriere directă de-acum încolo. Apelată din sdk.ts exact la
   * momentul `interactionStarted` (deschiderea widgetului sau prima
   * întrebare) - același punct care persistă `pendingGreeting`. Idempotentă:
   * a doua chemare nu face nimic.
   */
  flush(): void;
}

/**
 * Pornește ascultătorii. `isPageExcluded` vine din sdk.ts (același predicat care
 * ascunde widgetul și blochează snapshotul) - pe pagini excluse trail-ul e mut.
 *
 * Listener-ele stau pe document în faza de CAPTURE: un sit care face
 * stopPropagation în handler-ele lui (meniuri, SPA-uri) nu ne lasă altfel să
 * vedem click-ul deloc.
 *
 * Înregistrarea începe la ÎNCĂRCAREA paginii (nav-ul de aterizare, mai jos),
 * dar NU ajunge în sessionStorage până la prima interacțiune explicită a
 * vizitatorului - scutirea de consimțământ din ePrivacy art. 5(3) acoperă doar
 * stocarea „strict necesară unui serviciu cerut explicit de utilizator", nu o
 * scriere la fiecare încărcare de pagină pentru toți vizitatorii. Până atunci
 * evenimentele stau într-un buffer în memorie (moare cu documentul - pe un sit
 * cu mai multe pagini, traseul de dinainte de deschiderea chatului se pierde
 * la navigare; acceptat deliberat, nu de compensat cu altă formă de stocare).
 * `flush()` (întors mai jos) scrie bufferul acumulat și comută pe scriere
 * directă.
 */
export function installActionTrail(appId: string, isPageExcluded: () => boolean): ActionTrailHandle {
  let interacted = false;
  const buffer: UserActionEvent[] = [];

  const record = (event: Omit<UserActionEvent, "t">): void => {
    if (isPageExcluded()) return;
    const full: UserActionEvent = { ...event, t: Date.now() };
    if (interacted) {
      recordAction(appId, full);
      return;
    }
    buffer.push(full);
    if (buffer.length > ACTION_TRAIL_MAX_EVENTS) buffer.shift();
  };

  // pagina de aterizare e prima intrare din trail - drumul începe aici
  let lastPath = currentPagePath();
  record({ kind: "nav", path: lastPath });

  const onRouteChange = (): void => {
    const path = currentPagePath();
    if (path === lastPath) return;
    lastPath = path;
    record({ kind: "nav", path });
  };

  window.addEventListener("hashchange", onRouteChange);
  window.addEventListener("popstate", onRouteChange);
  // Rutarea SPA nu emite niciun eveniment - patch pe history, ca în sdk.ts
  // (dublul patch e inofensiv: fiecare împachetează ce găsește).
  for (const method of ["pushState", "replaceState"] as const) {
    const original = window.history[method].bind(window.history);
    window.history[method] = (...args: Parameters<History["pushState"]>) => {
      original(...args);
      onRouteChange();
    };
  }

  document.addEventListener(
    "click",
    (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest(OWN_ELEMENTS_SELECTOR)) return;
      const interactive = target.closest(INTERACTIVE_SELECTOR);
      if (!interactive || isExcludedElement(interactive)) return;
      const label = labelFor(interactive);
      record({ kind: "click", path: currentPagePath(), ...(label ? { label } : {}) });
    },
    true,
  );

  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.closest(OWN_ELEMENTS_SELECTOR) || isExcludedElement(form)) return;
      const label = formLabel(form);
      record({ kind: "submit", path: currentPagePath(), ...(label ? { label } : {}) });
    },
    true,
  );

  return {
    flush(): void {
      if (interacted) return;
      interacted = true;
      for (const event of buffer) recordAction(appId, event);
      buffer.length = 0;
    },
  };
}
